/* eslint-disable no-plusplus */
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import debounce from 'lodash/debounce';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { TextFieldState } from '~/types/enums';

import { hasProperty } from '../utils/types';

/**
 * @description
 * Validates based on passed in list of validation
 * ex: validations: { stringOnlyWithSpaces: true, required: true }
 * Dispatches required redux action either field value or null
 * Updates TextFieldStatem to either Error or Default based on validation result
 * Provides list of errors after validation
 * Handlers for onChange, onBlur and onFocus
 * OnChange is debounced validation (to avoid redux store update on every change)
 */

export const stringOnlyWithSpaces = /^[a-z\s]+$/i;
export const stringOnly = /^[a-z]+$/i;
export const alphaOrNumericWithSpaces = /^[a-z\d\s]+$/i;
export const alphaOrNumeric = /^[a-z\d]+$/i;
export const numericOnly = /^\d+$/;

const QBE_ABN = '78003191035';
const validateABN = (value: string) => {
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  const abn = value.replace(/[^\d]/, '');
  let isValidABNFormat = false;

  // check length is 11 digits
  if (abn.length === 11) {
    // apply ato check method
    let sum = 0;
    let weight;

    for (let index: number = 0; index <= weights.length - 1; index++) {
      weight = weights[index];
      const digit = Number(abn[index]) - (index ? 0 : 1);
      sum += weight * digit;
    }

    isValidABNFormat = sum % 89 === 0;
  }

  return isValidABNFormat;
};

/**
 * @TODO
 * remove dispatch from here,
 * update all files using action
 * remove stateUpdate
 */

export interface FieldValidation<ACTION> {
  validations: Validations;
  action?: ActionCreatorWithPayload<ACTION>;
  onValid?: (value: string | null) => void;
  onError?: (errorState: boolean) => void;
  debounceTime?: number;
}

export interface Validations {
  required?: boolean;
  maxLength?: number;
  minLength?: number;
  maxValue?: number;
  minValue?: number;
  pattern?: RegExp;
  stringOnly?: boolean;
  stringOnlyWithSpaces?: boolean;
  alphaNumeric?: boolean;
  alphaOrNumericWithSpaces?: boolean;
  numericOnly?: boolean;
  abnPattern?: boolean;
}

const validationLogic: {
  [key in keyof Validations]: (val: string, condition?: number | RegExp) => boolean;
} = {
  required: (val: string | number | undefined | null) =>
    typeof val === 'string' ? !!val : val !== null || true,
  maxLength: (val: string | number, maxLength) => val.toString().length <= Number(maxLength),
  minLength: (val: string | number, minLength) => val.toString().length >= Number(minLength),
  maxValue: (val: string, maxValue) => Number(val) <= Number(maxValue),
  minValue: (val: string, minValue) => Number(val) >= Number(minValue),
  pattern: (val: string, pattern) => hasProperty(pattern, 'test') && pattern?.test(val),
  stringOnly: (val: string) => stringOnly.test(val),
  stringOnlyWithSpaces: (val: string) => stringOnlyWithSpaces.test(val),
  alphaNumeric: (val: string) => alphaOrNumeric.test(val),
  alphaOrNumericWithSpaces: (val: string) => alphaOrNumericWithSpaces.test(val),
  numericOnly: (val: string) => numericOnly.test(val),
  abnPattern: (val: string) => validateABN(val) && val !== QBE_ABN,
};

const errorMessages: {
  [key in keyof Validations]: string;
} = {
  maxLength: 'maxLength error',
  minLength: 'minLength error',
  maxValue: 'maxValue error',
  minValue: 'minValue error',
  pattern: 'pattern error',
  required: 'required error',
  alphaNumeric: 'alphaNumeric error',
  numericOnly: 'numericOnly error',
  stringOnly: 'stringOnly error',
  abnPattern: 'abnPattern error',
};

const getErrors = (value: string, validations?: Validations): string[] => {
  if (validations) {
    return Object.entries(validations)
      .map(([k, condition]) => ({
        result: validationLogic[k as keyof Validations]?.(value, condition),
        message: errorMessages[k as keyof Validations],
      }))
      .map(({ result, message }) => (result ? null : message))
      .filter((v) => v !== null) as string[];
  }
  return [];
};

export const useFieldValidation = <ACTION>({
  validations,
  action,
  onValid,
  onError,
  debounceTime = 300,
}: FieldValidation<ACTION>) => {
  const [validationStatus, setValidationStatus] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [textFieldState, setTextFieldState] = useState<TextFieldState>(TextFieldState.Default);
  const dispatch = useDispatch();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const verify = useCallback(
    debounce((value) => {
      const errorList = getErrors(value, validations);
      const isValid = errorList?.length === 0;
      if (isValid) {
        setTextFieldState(TextFieldState.Default);
        if (action) dispatch(action(value));
        onValid?.(value);
        onError?.(false);
        setValidationStatus(true);
        setErrors([]);
      } else {
        setTextFieldState(TextFieldState.Error);
        if (action) dispatch(action(value));
        if (onError) onError(true);
        setValidationStatus(false);
        errorList && setErrors(errorList);
      }
    }, debounceTime),
    []
  );

  const debouncedChangeHandler = (fieldValue: string | number) => {
    verify(fieldValue);
  };

  const blurHandler = (fieldValue: string) => {
    const errorList = getErrors(fieldValue, validations);
    const isValid = errorList.length === 0;
    if (isValid) {
      setTextFieldState(TextFieldState.Default);
      setValidationStatus(true);
      setErrors([]);
    } else {
      setTextFieldState(TextFieldState.Error);
      setValidationStatus(false);
      setErrors(errorList);
    }
  };

  const focusHandler = () => setTextFieldState(TextFieldState.Default);

  return {
    debouncedChangeHandler,
    blurHandler,
    focusHandler,
    validationStatus,
    textFieldState,
    errors,
  };
};

export default useFieldValidation;
